import React, { forwardRef, ReactNode, RefObject } from "react";
import Icon, { IconName } from "../Icon/icon";
import "./buttons.scss";
import classNames from "classnames";
import { Utils, Intent } from "../../common";

export type ButtonSize = 24 | 32 | 40 | 48 | 56;

export interface ButtonProps {
  children?: ReactNode;
  intent?: Intent;
  size?: ButtonSize;
  text?: string;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  minimal?: boolean;
  outlined?: boolean;
  onBlur?: () => void;
  onClick?: () => void;
  onFocus?: () => void;
  type?: "submit" | "reset" | "button";
  href?: string;
  target?: string;
  icon?: IconName;
  rightIcon?: IconName;
  iconsOnly?: boolean;
}

// ❗️ 바꿀 때 AchorButton도 같이 수정해줘야 함
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      intent = "primary",
      size = 40,
      text,
      className,
      disabled = false,
      minimal = false,
      outlined = false,
      onClick,
      type = "button",
      icon,
      rightIcon,
      iconsOnly,
      ...buttonProps
    },
    ref
  ) => {
    const maybeHasText =
      !Utils.isReactNodeEmpty(text) || !Utils.isReactNodeEmpty(children);

    return (
      <button
        ref={ref as RefObject<HTMLButtonElement>}
        className={classNames({
          [`btn-${intent}`]: true,
          [`btn-${size}`]: size,
          [`${className}`]: className,
          "is-outlined": outlined,
          "is-minimal": minimal,
          "icon-only": iconsOnly,
        })}
        type={type}
        disabled={disabled}
        onClick={onClick}
        {...buttonProps}
      >
        {icon && <Icon name={icon} tabIndex={-1} />}

        {!iconsOnly && maybeHasText && (
          <span className="button-text">
            {text}
            {children}
          </span>
        )}

        {rightIcon && <Icon name={rightIcon} tabIndex={-1} />}
      </button>
    );
  }
);

export const AchorButton = forwardRef<HTMLAnchorElement, ButtonProps>(
  (
    {
      children,
      intent = "primary",
      size = 40,
      text,
      className,
      disabled = false,
      minimal = false,
      outlined = false,
      onClick,
      type = "button",
      icon,
      rightIcon,
      iconsOnly,
      href,
      target,
      ...buttonProps
    },
    ref
  ) => {
    const maybeHasText =
      !Utils.isReactNodeEmpty(text) || !Utils.isReactNodeEmpty(children);

    return (
      <a
        ref={ref as RefObject<HTMLAnchorElement>}
        className={classNames({
          [`btn-${intent}`]: true,
          [`btn-${size}`]: size,
          [`${className}`]: className,
          "is-outlined": outlined,
          "is-minimal": minimal,
          "is-disabled": disabled,
          "icon-only": iconsOnly,
        })}
        type={type}
        role="button"
        href={disabled ? undefined : href}
        target={target}
        {...buttonProps}
      >
        {icon && <Icon name={icon} tabIndex={-1} />}
        {!iconsOnly && maybeHasText && (
          <span className="button-text">
            {text}
            {children}
          </span>
        )}

        {rightIcon && <Icon name={rightIcon} tabIndex={-1} />}
      </a>
    );
  }
);

